import { BaseController } from "../../../../core/controllers/base.controller";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { combineLatest } from "rxjs/observable/combineLatest";
import { ContactControlModel, IContactControlModel } from "./contact-control.model";
import { ContactFieldFormValue } from "./contact-control-form-value.model";
import { Contact } from "../../../business-objects/contact/contact.model";
import { ControlContact } from "../contact-control.model";
import { FormControl, FormGroup } from "@angular/forms";
import { IContactFormArgs } from "../../../pages/contact-form/contact-form.controller";
import { IContactPickerComponentArgs } from "../../../pages/contact-picker/contact-picker";
import { Injectable } from "@angular/core";
import { isDefined } from "ionic-angular/util/util";
import { ModalController } from "ionic-angular";
import { Observable } from "rxjs/Observable";
import { VasAppFacade } from "../../../services/vas-app-facade.service";

export interface IContactControlControllerParams {
    control: ControlContact;
}

@Injectable()
export class ContactControlController extends BaseController {

    protected control: ControlContact;

    protected formGroup: FormGroup;

    protected _model$ = new BehaviorSubject<ContactControlModel>(new ContactControlModel({
        formGroup: this.makeContactFormGroup()
    }));

    constructor(
        protected appFacade: VasAppFacade,
        protected modalCtrl: ModalController
    ) {
        super();
    }

    get model$(): Observable<ContactControlModel> {
        return this._model$.takeUntil(this.ngUnsubscribe).filter(value => !!value);
    }

    protected get model(): ContactControlModel {
        return this._model$.value;
    }

    onInit(params: IContactControlControllerParams): void {
        this.control = params.control;
        combineLatest(
            this.appFacade.objects.contact.store.getAll(),
            this.appFacade.objects.account.store.getActive()
        )
            .takeUntil(this.ngUnsubscribe)
            .map(([contacts, account]) => {
                return new ContactControlModel({ ...this.model.values, contacts, account });
            })
            .subscribe(model => this._model$.next(model));
    }

    onWriteValue(value: ContactFieldFormValue): void {
        if (!value) {
            return;
        }

        this.model$
            .filter(model => isDefined(model.account))
            .take(1)
            .subscribe(model => {
            const contact = new Contact({ ...value, account: model.account.id, id: value.contact });
            this.updateModelWithContactFormGroup({ contact });
        });
    }

    onShowContactPicker(model: ContactControlModel): void {
        // const modal = this.modalCtrl.create("ListPickerControlModalComponent", {
        //     config: new ListPickerControlConfig({
        //         listType: LIST_DEFINITIONS.CONTACT_ALL,
        //         listTypeParameters: [],
        //         multi: false
        //     }),
        //     initialValue: undefined
        // } as IListPickerControlModalComponentArgs);
        const modal = this.modalCtrl.create("ContactPickerComponent", { control: this.control } as IContactPickerComponentArgs);

        modal.onDidDismiss(value => {
            if (isDefined(value)) {
                const contact = model.contacts.find(item => item.id === value);
                this.updateModelWithContactFormGroup({ contact });
            }
        });

        modal.present();
    }

    openContactForm(model: ContactControlModel): void {
        const modal = this.modalCtrl.create("ContactPreFilledFormModal", {
            accountId: model.account.id,
            contact: this.contactFromFormGroup()
        } as IContactFormArgs);

        modal.onDidDismiss((contact?: Contact) => {
            if (isDefined(contact)) {
                this.updateModelWithContactFormGroup({ contact });
            }
        });

        modal.present();
    }

    updateContactData(model: ContactControlModel): void {
        const values = this.contactFromFormGroup();

        const contact = new Contact({
            ...model.contact,
            ...values
        });

        this.updateModelWithContactFormGroup({ contact });
        this.appFacade.objects.contact.store.update(contact);
    }

    protected updateModelWithContactFormGroup(args: IContactControlModel): void {
        this._model$.next(new ContactControlModel({ ...this.model.values, ...args }));
        this.updateContactFormGroup(args.contact);
    }

    protected makeContactFormGroup(): FormGroup {
        // @todo this should use src/vas/business-objects/contact/contact.helper.ts
        return new FormGroup({
            contact: new FormControl(""),
            business: new FormControl(""),
            name: new FormControl(""),
            address: new FormControl(""),
            address2: new FormControl(""),
            address3: new FormControl(""),
            phone: new FormControl(""),
            mobile: new FormControl(""),
            email: new FormControl(""),
            website: new FormControl(""),
            vatNumber: new FormControl(""),
            billingCode: new FormControl("")
        });
    }

    protected updateContactFormGroup(contact: Contact): void {
        if (isDefined(contact) && contact) {
            this.model.formGroup.patchValue({
                contact: contact.id,
                business: contact.business,
                name: contact.name,
                address: contact.address,
                address2: contact.address2,
                address3: contact.address3,
                phone: contact.phone,
                fax: contact.fax,
                mobile: contact.mobile,
                email: contact.email,
                website: contact.website,
                contactType: contact.contactType,
                vatNumber: contact.vatNumber,
                billingCode: contact.billingCode
            } as ContactFieldFormValue);
        }
    }

    protected contactFromFormGroup(): Contact {
        const args = this.model.formGroup.value;
        delete args.contact;
        return args;
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/contact/form/contact-control.controller.ts